import React, { useEffect, useRef } from 'react';
import { useObservable } from 'react-use-observable';

import { App, Button, Modal, Spin, Typography } from 'antd';

import { Field, Form, Formik } from 'formik';
import type { FormikProps } from 'formik';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import NoDangerouslyHTML from 'v2/components/Shared/NoDangerouslyHTML';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateMomentFormat } from 'v2/formatters/date';
import { ETicketStatus } from 'v2/interfaces/help';
import helpService from 'v2/services/help';

import { historyModalSchema } from './validation';

interface IProps {
  isOpen: boolean;
  handleClose: () => void;
}

interface IFormikValues {
  comment: string;
}

const HistoryModal = ({ isOpen, handleClose }: IProps) => {
  const { message } = App.useApp();
  const historyChatRef = useRef<HTMLDivElement>();

  const [loadingTicket] = useObservable(() => helpService.getLoadingTicket(), []);
  const [selectedTicket] = useObservable(() => helpService.getTicket(), []);

  const canComment = selectedTicket?.status !== ETicketStatus.Fechado;

  const onSubmit = ({ comment }: IFormikValues, { setSubmitting, resetForm }: FormikProps<IFormikValues>) => {
    helpService.sendComment(selectedTicket.id, comment).subscribe(
      () => {
        message.success('Comentário enviado com sucesso!');
        resetForm();
        setSubmitting(false);
      },
      () => {
        message.error('Erro ao enviar comentário!');
        setSubmitting(false);
      }
    );
  };

  useEffect(() => {
    const historyChatNode = historyChatRef.current;

    if (historyChatNode) {
      historyChatNode.scrollTop = historyChatNode.scrollHeight - historyChatNode.clientHeight;
    }
  }, [loadingTicket]);

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      title='Histórico do ticket'
      width={600}
      footer={
        <div className='flex justify-end'>
          <Button type='text' onClick={handleClose}>
            Fechar
          </Button>
        </div>
      }
    >
      {loadingTicket && (
        <div className='flex justify-center py-8'>
          <Spin />
        </div>
      )}
      {!loadingTicket && (
        <div className='flex flex-col gap-4'>
          <Typography.Text strong>
            <Truncate count={50} final='...'>
              {selectedTicket?.title}
            </Truncate>
          </Typography.Text>

          <div
            ref={historyChatRef}
            className='max-h-67.5 w-full overflow-auto rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'
          >
            {selectedTicket?.comments?.map(comment => (
              <div
                key={'message-' + comment.id}
                className={`mb-3 flex w-full max-w-75 flex-col last:mb-0 ${comment.self ? 'ml-auto items-end' : 'items-start'}`}
              >
                {!comment.self && (
                  <Typography.Text type='secondary' className='mb-1 text-xs'>
                    Suporte Eduzz
                  </Typography.Text>
                )}
                <NoDangerouslyHTML
                  className={`inline-block max-w-full rounded-(--uizz-antd-border-radius) px-4 py-2 text-sm ${
                    comment.self
                      ? 'bg-(--uizz-antd-color-success-bg) text-(--uizz-antd-color-success)'
                      : 'bg-(--uizz-antd-color-info-bg) text-(--uizz-antd-color-info)'
                  }`}
                  html={comment.content}
                />
                <Typography.Text type='secondary' className='mt-1 text-xs'>
                  {dateMomentFormat(comment.created_at, 'DD/MM/YYYY HH:mm')}
                </Typography.Text>
              </div>
            ))}
          </div>

          {canComment && (
            <Formik initialValues={{ comment: '' }} onSubmit={onSubmit} validationSchema={historyModalSchema}>
              {({ dirty, isValid, isSubmitting }) => (
                <Form className='flex flex-col gap-4'>
                  <Field
                    name='comment'
                    placeholder='Digite um comentário...'
                    fullWidth
                    multiline
                    rows={2}
                    inputProps={{
                      maxLength: 500
                    }}
                    component={TextFormField}
                  />
                  <div className='flex justify-end'>
                    <Button type='primary' disabled={!dirty || !isValid} htmlType='submit' loading={isSubmitting}>
                      Comentar
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      )}
    </Modal>
  );
};

export default HistoryModal;
